/**
 * Reports — Operating metrics (Reports Phase B / B.2, migrations 0151 + 0153).
 *
 * The durable, shareable core of the Monday Cowork operating review, native in
 * Peace OS. Layout: hero tiles (revenue w/ WoW, and the plan verdict when the
 * month has a plan) → weekly revenue stacked → monthly line + table → CM
 * weekly → SPICE weekly → clinician hours → top clients → sources footnote.
 *
 * Weeks on or after the tenant's billing.native_from are summed live from
 * Olive's billing ledger by the server (origin 'billing'); earlier weeks are
 * the uploaded rows (origin 'upload'). Revenue is billable revenue by service
 * date. The page carries no cash, AR, AR aging, collections, cash+AR or
 * expenses piece and no upload door; the ingest route and its table stay on
 * the server.
 *
 * The richer surfaces ride the 0153 `sections` jsonb bag (payload v2). Each
 * section renders ONLY when present — an absent section hides its card and the
 * page degrades gracefully to the core weekly view. Derived surfaces (WoW,
 * verdict, CM/SPICE weekly stats, per-clinician stats) need
 * NO new data — they are computed from what is already on the wire.
 *
 * STANDING RULES honored here: plan-vs-actual is ALWAYS a BAND (calendar-day
 * and working-day proration bounds for the in-progress month) — never a
 * single-number verdict; and there are NO extrapolated projections. `status`
 * renders as a quiet chip (preliminary / restated).
 *
 * Owner-only: canViewReports gates the page (the read; board_observer reads
 * under 0152). Charts are hand-rolled (div bars + inline-SVG polylines), no
 * dependency.
 */

import { type ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { canViewReports } from '@/lib/capabilities';
import { operatingMetricsApi, type OperatingMetricsReportWire, type WeeklyOperatingMetricWire } from '@/lib/api';
import { PageShell } from '@/components/primitives/PageShell';
import {
  money,
  moneyNum,
  hours,
  signedPct,
  formatWeekEnd,
  monthBand,
  verdictLabel,
  wowStats,
  latestCompletedIndex,
  monthlyAggregates,
} from '@/components/reports/operatingDerive';
import {
  WeeklyBarChart,
  MonthlyLineChart,
  ClinicianHoursCard,
  TopClientsCard,
  SourcesFootnote,
} from '@/components/reports/OperatingSections';

const WEEKS = 12;

const FOREST = 'var(--color-sage-800, #1B4332)';
const GOLD = 'var(--color-warm-400, #C4A265)';

// ---------------------------------------------------------------------
// Status chip
// ---------------------------------------------------------------------

function StatusChip({ status }: { status: WeeklyOperatingMetricWire['status'] }): ReactNode {
  if (status === 'preliminary') {
    return (
      <span
        data-testid="status-chip"
        data-status="preliminary"
        className="inline-flex items-center rounded-full bg-sage-800/[6%] px-2 py-0.5 text-[11px] font-medium text-ink-soft"
      >
        Preliminary
      </span>
    );
  }
  if (status === 'restated') {
    return (
      <span
        data-testid="status-chip"
        data-status="restated"
        className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700"
      >
        Restated
      </span>
    );
  }
  return (
    <span
      data-testid="status-chip"
      data-status="final"
      className="inline-flex items-center rounded-full bg-sage-800/[10%] px-2 py-0.5 text-[11px] font-medium text-sage-800"
    >
      Final
    </span>
  );
}

// ---------------------------------------------------------------------
// Headline cards
// ---------------------------------------------------------------------

function HeadlineCard({
  label,
  value,
  sub,
  children,
  testId,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  children?: ReactNode;
  testId: string;
}): ReactNode {
  return (
    <div
      data-testid={testId}
      className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4 flex flex-col gap-1"
    >
      <div className="text-[11.5px] font-semibold uppercase tracking-wider text-sage-400">{label}</div>
      <div className="font-display text-2xl text-sage-800 tabular-nums">{value}</div>
      {sub && <div className="text-xs text-ink-soft">{sub}</div>}
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------
// 12-week stacked CM/SPICE bars + hours line (the existing weekly chart)
// ---------------------------------------------------------------------

function RevenueTrend({ rows }: { rows: WeeklyOperatingMetricWire[] }): ReactNode {
  const weeks = [...rows].reverse();
  const maxRevenue = weeks.reduce((m, r) => {
    const cm = r.cm_revenue === null ? 0 : Number(r.cm_revenue);
    const sp = r.spice_revenue === null ? 0 : Number(r.spice_revenue);
    return Math.max(m, cm + sp);
  }, 0);
  const maxHours = weeks.reduce((m, r) => Math.max(m, r.total_hours === null ? 0 : Number(r.total_hours)), 0);

  const hoursPoints = weeks
    .map((r, i) => {
      if (r.total_hours === null || maxHours === 0) return null;
      const x = weeks.length === 1 ? 50 : (i / (weeks.length - 1)) * 100;
      const y = 100 - (Number(r.total_hours) / maxHours) * 100;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .filter((p): p is string => p !== null)
    .join(' ');

  return (
    <div data-testid="revenue-trend" className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-sage-800">Revenue by week</h2>
        <div className="flex items-center gap-3 text-[11px] text-ink-soft">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: FOREST }} /> CM
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: GOLD }} /> SPICE
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-3 rounded-full bg-amber-600" /> Hours
          </span>
        </div>
      </div>
      <div className="relative h-48">
        {hoursPoints && (
          <svg
            data-testid="hours-line"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full pointer-events-none"
          >
            <polyline points={hoursPoints} fill="none" stroke="#d97706" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          </svg>
        )}
        <div className="flex items-end gap-1.5 h-full">
          {weeks.map((r) => {
            const cm = r.cm_revenue === null ? 0 : Number(r.cm_revenue);
            const sp = r.spice_revenue === null ? 0 : Number(r.spice_revenue);
            const cmPct = maxRevenue === 0 ? 0 : (cm / maxRevenue) * 100;
            const spPct = maxRevenue === 0 ? 0 : (sp / maxRevenue) * 100;
            return (
              <div
                key={r.week_ending}
                data-testid="revenue-bar"
                className="flex-1 h-full flex flex-col justify-end items-stretch"
                title={`Week ending ${formatWeekEnd(r.week_ending)}: CM ${money(r.cm_revenue)}, SPICE ${money(r.spice_revenue)}, ${hours(r.total_hours)}`}
              >
                <div className="w-full rounded-t-tight" style={{ height: `${spPct}%`, backgroundColor: GOLD }} />
                <div className="w-full" style={{ height: `${cmPct}%`, backgroundColor: FOREST }} />
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex gap-1.5 mt-1.5">
        {weeks.map((r) => (
          <div key={r.week_ending} className="flex-1 text-center text-[10px] leading-tight text-ink-soft">
            {formatWeekEnd(r.week_ending)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Monthly actual vs plan (band) vs prior year — the table (weekly-derived)
// ---------------------------------------------------------------------

function MonthlyTable({
  rows,
  monthly,
}: {
  rows: WeeklyOperatingMetricWire[];
  monthly?: Array<{ month: string; actual?: string | null; plan?: string | null; prior_year?: string | null }> | null;
}): ReactNode {
  const months = monthlyAggregates(rows, monthly);
  const currentMonthKey = months[0]?.key;
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <h2 className="font-display text-lg text-sage-800 mb-1">Monthly · actual vs plan vs prior year</h2>
      <p className="text-xs text-ink-soft mb-3">
        The current month&apos;s plan is a proration band (calendar-day and working-day bounds), never a single-number
        verdict.
      </p>
      <div className="overflow-x-auto">
        <table data-testid="monthly-table" className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11.5px] font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]">
              <th className="py-2 pr-3">Month</th>
              <th className="py-2 px-3 text-right">Actual</th>
              <th className="py-2 px-3 text-right">Plan</th>
              <th className="py-2 pl-3 text-right">Prior year</th>
            </tr>
          </thead>
          <tbody>
            {months.map((mo) => {
              const isCurrent = mo.key === currentMonthKey;
              const band = isCurrent ? monthBand(rows) : null;
              return (
                <tr key={mo.key} className="border-b border-sage-800/[5%] last:border-0">
                  <td className="py-2 pr-3 text-ink">{mo.label}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-ink font-semibold">{moneyNum(mo.actual)}</td>
                  <td className="py-2 px-3 text-right tabular-nums text-ink">
                    {isCurrent && band && band.hasPlan ? (
                      <span data-testid="plan-band" title="Calendar-day to working-day proration band">
                        {moneyNum(Math.round(band.bandLow!))} – {moneyNum(Math.round(band.bandHigh!))}
                      </span>
                    ) : mo.plan !== null ? (
                      moneyNum(mo.plan)
                    ) : (
                      '–'
                    )}
                  </td>
                  <td className="py-2 pl-3 text-right tabular-nums text-ink-soft">
                    {mo.priorYear === null ? '–' : moneyNum(mo.priorYear)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Plan-verdict hero tile
// ---------------------------------------------------------------------

/** Renders only when the latest week's month has a plan on file; with no
 *  plan (every live week from Olive billing) the tile is absent, never a
 *  zero verdict. */
function VerdictTile({ rows }: { rows: WeeklyOperatingMetricWire[] }): ReactNode {
  const band = useMemo(() => monthBand(rows), [rows]);
  if (!band || !band.hasPlan) return null;

  // Pace: MTD as a fraction of the calendar- and working-day prorated plan.
  const calPace = band.monthPlan > 0 ? (band.mtdActual / (band.monthPlan * band.calFrac)) * 100 : null;
  const workPace = band.monthPlan > 0 ? (band.mtdActual / (band.monthPlan * band.workFrac)) * 100 : null;

  return (
    <HeadlineCard
      testId="card-verdict"
      label={`${band.month} vs plan`}
      value={moneyNum(Math.round(band.mtdActual))}
      sub={
        <div className="space-y-0.5">
          <div className="font-medium text-ink">{verdictLabel(band.verdict)}</div>
          <div data-testid="headline-plan-band">
            plan band {moneyNum(Math.round(band.bandLow!))} – {moneyNum(Math.round(band.bandHigh!))}
          </div>
          {calPace !== null && workPace !== null && (
            <div className="text-[11px]">
              MTD {moneyNum(Math.round(band.mtdActual))} = {Math.round(calPace)}% of calendar pace, {Math.round(workPace)}% of working-day pace
            </div>
          )}
        </div>
      }
    />
  );
}

// ---------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------

export function OperatingReports(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const allowed = canViewReports(whoami);

  const query = useQuery<OperatingMetricsReportWire>({
    queryKey: ['reports-operating-weekly', tenantSlug, WEEKS],
    queryFn: ({ signal }) => operatingMetricsApi.weekly(tenantSlug, { weeks: WEEKS }, signal),
    staleTime: 60_000,
    enabled: allowed,
  });

  const data = query.data;
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const isEmpty = !!data && rows.length === 0;
  const latest = rows[0];
  // The hero tile reads the newest completed week; the running week stays in
  // the charts and the header line. With no completed week yet, the tile shows
  // the running week as "so far this week", with no week-over-week figure.
  const completedIdx = useMemo(() => latestCompletedIndex(rows), [rows]);
  const heroRow = completedIdx === null ? latest : rows[completedIdx];
  const wow = useMemo(() => wowStats(completedIdx === null ? [] : rows.slice(completedIdx)), [rows, completedIdx]);

  // The newest week that carries sections drives the richer cards (per-week
  // bag). A live week from Olive billing never carries one, so reading the
  // latest row alone dropped the monthly plan and prior year the moment the
  // first live week landed; the newest uploaded week keeps them. The
  // clinician-hours surface reads ACROSS weeks. No row with sections -> the
  // cards hide (graceful degradation).
  const sections = rows.find((r) => r.sections != null)?.sections ?? null;

  return (
    <PageShell
      testId="reports-operating-main"
      maxWidth="wide"
      eyebrow="Olive · Operations"
      title="Dashboard"
      access={{ allowed }}
      async={{ what: 'the reports',
        isLoading: allowed && query.isPending,
        isError: query.isError,
        error: query.error,
        onRetry: () => void query.refetch(),
        isEmpty,
        emptyHeadline: "Once Olive bills a week, you'll see its revenue here.",
      }}
    >
      <div className="space-y-6">
        {latest && (
          <>
            <div className="flex items-center gap-2 text-sm text-ink-soft">
              <span>
                Latest week ending <span className="font-medium text-ink">{formatWeekEnd(latest.week_ending)}</span>
              </span>
              <StatusChip status={latest.status} />
              {latest.source_note && <span className="text-xs italic">· {latest.source_note}</span>}
            </div>

            {/* Hero tiles: revenue (w/ WoW), and the plan verdict when the month has a plan */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <HeadlineCard
                testId="card-revenue"
                label={completedIdx === null ? 'This week revenue' : 'Last week revenue'}
                value={money(heroRow!.total_revenue)}
                sub={`CM ${money(heroRow!.cm_revenue)} · SPICE ${money(heroRow!.spice_revenue)}`}
              >
                {completedIdx === null ? (
                  <div data-testid="revenue-so-far" className="text-[11px] text-ink-soft mt-0.5">
                    so far this week
                  </div>
                ) : (
                  <div data-testid="revenue-wow" className="text-[11px] text-ink-soft mt-0.5">
                    WoW {signedPct(wow.wowPct)} · vs 12-wk avg {signedPct(wow.vs12wkPct)}
                  </div>
                )}
              </HeadlineCard>
              <VerdictTile rows={rows} />
            </div>

            {/* Weekly revenue stacked */}
            <RevenueTrend rows={rows} />

            {/* Monthly line (sections) + the weekly-derived table */}
            {sections?.monthly && sections.monthly.length > 0 && (
              <MonthlyLineChart monthly={sections.monthly} testId="monthly-line" />
            )}
            <MonthlyTable rows={rows} monthly={sections?.monthly ?? null} />

            {/* CM weekly + SPICE weekly (derived from core columns) */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <WeeklyBarChart title="CM revenue · weekly" rows={rows} pick={(r) => r.cm_revenue} color={FOREST} testId="cm-weekly" />
              <WeeklyBarChart title="SPICE revenue · weekly" rows={rows} pick={(r) => r.spice_revenue} color={GOLD} testId="spice-weekly" />
            </div>

            {/* Clinician hours across weeks (sections.clinician_hours per week);
                CM Utilization + hours flag use roster targets from Kith. */}
            <ClinicianHoursCard
              rows={rows}
              note={sections?.hours_note ?? null}
              rosterTargets={data?.roster_targets ?? []}
              testId="clinician-hours"
            />

            {/* Top revenue clients (sections.top_clients) — replaces quiet clients */}
            {sections?.top_clients && sections.top_clients.length > 0 && (
              <TopClientsCard topClients={sections.top_clients} testId="top-clients" />
            )}

            {/* Sources footnote (sections.sources_note) */}
            {sections?.sources_note && <SourcesFootnote note={sections.sources_note} testId="sources-note" />}
          </>
        )}
      </div>
    </PageShell>
  );
}
